import React, { useContext, useEffect, useState } from "react";
import { Plan } from "../../../@types";
import StarRatingRow from "./StarRatingRow";
import StarRatingList from "./StarRatingList";
import StarRatingListItem from "./StarRatingListItem";
import { getStarRating } from "../../../helpers/starRatingHelpers";
import { useTranslate } from "../../../helpers/intlHooks";
import { StarRating } from "../../../components";
import { AppContext } from "../../../app/store";
import { AnalyticsActionType } from "../../../app/contexts/Analytics/types";

export const drugStarRatingsListItemTestId = "drug-star-ratings";

export enum DrugStarRatingRowCategories {
  STAR_DRUG_CUSTOMER_SERVICE_RATING = "STAR_DRUG_CUSTOMER_SERVICE_RATING",
  STAR_DRUG_COMPLAINTS_RATING = "STAR_DRUG_COMPLAINTS_RATING",
  STAR_DRUG_MEMBER_EXP_RATING = "STAR_DRUG_MEMBER_EXP_RATING",
  STAR_PROVIDES_PRICING_RATING = "STAR_PROVIDES_PRICING_RATING",
}

export enum DrugStarRatingComplaintCategories {
  STAR_DRUG_PLAN_COMPLAINTS_RATING = "STAR_DRUG_PLAN_COMPLAINTS_RATING",
  STAR_DRUG_MEMBERS_LEAVING_RATING = "STAR_DRUG_MEMBERS_LEAVING_RATING",
  STAR_DRUG_IMPROVEMENT_RATING = "STAR_DRUG_IMPROVEMENT_RATING",
}

export enum DrugStarRatingCustomerServiceCategories {
  STAR_DRUG_TTY_AND_FL_RATING = "STAR_DRUG_TTY_AND_FL_RATING",
}

export enum DrugStarRatingMemberExperienceCategories {
  STAR_DRUG_PLAN_MEMBER_RATING = "STAR_DRUG_PLAN_MEMBER_RATING",
  STAR_PRESCRIPTION_RATING = "STAR_PRESCRIPTION_RATING",
}

export enum DrugStarRatingPricingAndSafetyCategories {
  STAR_DRUG_ACCURACY_RATING = "STAR_DRUG_ACCURACY_RATING",
  STAR_DIABETES_RATING = "STAR_DIABETES_RATING",
  STAR_BLOOD_PRESSURE_RATING = "STAR_BLOOD_PRESSURE_RATING",
  STAR_CHOLESTEROL_RATING = "STAR_CHOLESTEROL_RATING",
  STAR_PHARMACIST_RATING = "STAR_PHARMACIST_RATING",
  STAR_DIABETES_CHOLESTEROL_RATING = "STAR_DIABETES_CHOLESTEROL_RATING",
}

export enum DrugStarRatingSummaryCategory {
  STAR_DRUG_PLAN_RATING = "STAR_DRUG_PLAN_RATING",
}

export const DrugStarRatingCategories = {
  ...DrugStarRatingRowCategories,
  ...DrugStarRatingComplaintCategories,
  ...DrugStarRatingCustomerServiceCategories,
  ...DrugStarRatingMemberExperienceCategories,
  ...DrugStarRatingPricingAndSafetyCategories,
  ...DrugStarRatingSummaryCategory,
};

const DrugStarRatings: React.FunctionComponent<{
  plan: Plan;
  expandAll?: boolean;
}> = ({ plan, expandAll = false }) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const [drugPlanExpanded, setDrugPlanExpanded] = useState(expandAll);
  const [customerServiceExpanded, setCustomerServiceExpanded] = useState(
    expandAll
  );
  const [complaintsExpanded, setComplaintsExpanded] = useState(expandAll);
  const [memberExperienceExpanded, setMemberExperienceExpanded] = useState(
    expandAll
  );
  const [safetyExpanded, setSafetyExpanded] = useState(expandAll);

  const toggleDrugPlanExpanded = (): void => {
    setDrugPlanExpanded(!drugPlanExpanded);

    if (!drugPlanExpanded) {
      setCustomerServiceExpanded(false);
      setComplaintsExpanded(false);
      setMemberExperienceExpanded(false);
      setComplaintsExpanded(false);
      setSafetyExpanded(false);

      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Plan Details - Star Ratings",
          event_label: "Toggle - Drug Plan Star Ratings",
        },
      });
    }
  };

  useEffect(() => {
    setDrugPlanExpanded(expandAll);
    setCustomerServiceExpanded(expandAll);
    setComplaintsExpanded(expandAll);
    setMemberExperienceExpanded(expandAll);
    setComplaintsExpanded(expandAll);
    setSafetyExpanded(expandAll);
  }, [expandAll]);

  return (
    <div data-testid="drug-plan-ratings">
      <StarRatingRow
        label={t("plan_stars.drug.star_ratings")}
        open={drugPlanExpanded}
        onClick={toggleDrugPlanExpanded}
        color="green"
        plan={plan}
        ariaLabel={t("star_rating.drug_plan_aria")}
      >
        {/* Customer Service */}
        <StarRatingRow
          data-testid={drugStarRatingsListItemTestId}
          label={t("plan_stars.drug.customer_service")}
          open={customerServiceExpanded}
          starRating={getStarRating(
            plan,
            DrugStarRatingCategories.STAR_DRUG_CUSTOMER_SERVICE_RATING
          )}
          onClick={(): void => {
            setCustomerServiceExpanded(!customerServiceExpanded);
            if (!customerServiceExpanded) {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Plan Details - Star Ratings",
                  event_label: "Toggle - Drug Plan customer service",
                },
              });
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.customer_service.tty")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DRUG_TTY_AND_FL_RATING
              )}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Member Complaints */}
        <StarRatingRow
          data-testid={drugStarRatingsListItemTestId}
          label={t("plan_stars.drug.complaints")}
          open={complaintsExpanded}
          starRating={getStarRating(
            plan,
            DrugStarRatingCategories.STAR_DRUG_COMPLAINTS_RATING
          )}
          onClick={(): void => {
            setComplaintsExpanded(!complaintsExpanded);
            if (!complaintsExpanded) {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Plan Details - Star Ratings",
                  event_label: "Toggle - Drug Plan member complaints",
                },
              });
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.complaints.complaints")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DRUG_PLAN_COMPLAINTS_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.complaints.leaving")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DRUG_MEMBERS_LEAVING_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.complaints.improvement")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DRUG_IMPROVEMENT_RATING
              )}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Member Experience */}
        <StarRatingRow
          data-testid={drugStarRatingsListItemTestId}
          label={t("plan_stars.drug.experience")}
          open={memberExperienceExpanded}
          starRating={getStarRating(
            plan,
            DrugStarRatingCategories.STAR_DRUG_MEMBER_EXP_RATING
          )}
          onClick={(): void => {
            setMemberExperienceExpanded(!memberExperienceExpanded);
            if (!memberExperienceExpanded) {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Plan Details - Star Ratings",
                  event_label: "Toggle - Drug Plan member experience",
                },
              });
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.experience.rating")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DRUG_PLAN_MEMBER_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.experience.prescription")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_PRESCRIPTION_RATING
              )}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>

        {/* Drufy Safety and Pricing */}
        <StarRatingRow
          data-testid={drugStarRatingsListItemTestId}
          label={t("plan_stars.drug.safety")}
          open={safetyExpanded}
          starRating={getStarRating(
            plan,
            DrugStarRatingCategories.STAR_PROVIDES_PRICING_RATING
          )}
          onClick={(): void => {
            setSafetyExpanded(!safetyExpanded);
            if (!safetyExpanded) {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Plan Details - Star Ratings",
                  event_label: "Toggle - Drug Plan safety and pricing",
                },
              });
            }
          }}
          plan={plan}
        >
          <StarRatingList>
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.safety.pricing")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DRUG_ACCURACY_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.safety.diabetes")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DIABETES_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.safety.blood_pressure")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_BLOOD_PRESSURE_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.safety.cholesterol")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_CHOLESTEROL_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.safety.pharmacist")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_PHARMACIST_RATING
              )}
              planYear={plan.contract_year}
            />
            <StarRatingListItem
              data-testid={drugStarRatingsListItemTestId}
              label={t("plan_stars.drug.safety.diabetes_cholesterol")}
              starRating={getStarRating(
                plan,
                DrugStarRatingCategories.STAR_DIABETES_CHOLESTEROL_RATING
              )}
              planYear={plan.contract_year}
            />
          </StarRatingList>
        </StarRatingRow>
      </StarRatingRow>

      {/* Summary */}
      <div className={`StarRatingRow StarRatingRow__blue`}>
        <h3 className={"StarRatingsSection__heading"}>
          {t("plan_stars.drug.summary")}
        </h3>
        <StarRating
          data-testid={drugStarRatingsListItemTestId}
          stars={getStarRating(
            plan,
            DrugStarRatingCategories.STAR_DRUG_PLAN_RATING
          )}
          includeLabel={false}
          planYear={plan.contract_year}
        />
      </div>
    </div>
  );
};

export default DrugStarRatings;
